import { useState, useEffect } from 'react';
import { UseWindowDimensionsReturn, WindowSize } from 'common/hooks/types';
import { mobileWidthLimit, tabletWidthLimit } from '../constants';

function getWindowSize(width: number): WindowSize {
  if (width <= mobileWidthLimit) {
    return 'mobile';
  }
  if (width <= tabletWidthLimit) {
    return 'tablet';
  }
  return 'desktop';
}

/**
 * The responsive breakpoint the window is in. Only the DERIVED breakpoint is
 * held in state, and it only changes when the window actually crosses one.
 *
 * This used to keep the raw {width, height} in state as a fresh object on
 * every resize event, and ResponsiveContext handed that to ~40 consumers.
 * On iOS the Capacitor keyboard resizes the webview natively, so every
 * keyboard show/hide (every focus change in a form) re-rendered most of the
 * app mid-animation - the judder in the add-vehicle sheet. Anything that
 * genuinely needs a live measurement should take it locally (see
 * useWindowWidthAtLeast) instead of putting it back in context.
 */
export const useWindowDimensions = (): UseWindowDimensionsReturn => {
  const [windowSize, setWindowSize] = useState<WindowSize>(() => getWindowSize(window.innerWidth));

  useEffect(() => {
    let frame = 0;
    function handleResize() {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = getWindowSize(window.innerWidth);
        setWindowSize((prev) => (prev === next ? prev : next));
      });
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    windowSize,
    isDesktopView: windowSize === 'desktop',
  };
};

/**
 * True while the window is at least `minWidth` px wide. Re-renders the
 * caller only when that answer flips - a height-only resize (the iOS
 * keyboard) never re-renders it.
 */
export const useWindowWidthAtLeast = (minWidth: number): boolean => {
  const [atLeast, setAtLeast] = useState(() => window.innerWidth >= minWidth);

  useEffect(() => {
    const check = () => {
      const next = window.innerWidth >= minWidth;
      setAtLeast((prev) => (prev === next ? prev : next));
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [minWidth]);

  return atLeast;
};
